import { ConfigProvider, DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import FullPageLoader from "../../../core/common/loader/FullPageLoader";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import {
  deleteFileFromAWS,
  uploadFileToAWS,
} from "../../../core/data/redux/nocExIndiaSlice";
import { fetchNOCHigherEducationRequest } from "../../../core/data/redux/nocHigherEducationSlice";
import Api from "../../../core/data/redux/utils/apiCall";
import { all_routes } from "../../router/all_routes";

const { RangePicker: TimeRangePicker } = TimePicker;

dayjs.extend(customParseFormat);

const validationSchema = Yup.object().shape({
  probationConfirmed: Yup.string().required("Please select probation status"),
  currentlyPursuingA: Yup.string().required("Please select pursuing status"),
  natureOfDutyPerforming: Yup.string().required("Please select nature of duty"),
  nameOfStudyCourse: Yup.string().required("Course name is required"),
  typeOfCourse: Yup.string().required("Type of course is required"),
  durationMonths: Yup.string().required("Duration of course (Months) is required"),
  dateOfJoiningCourse: Yup.string().required("Date of joining is required"),
  institutionName: Yup.string().required("Institution name is required"),
  lastDateRegistration: Yup.string().required(
    "Last date of registration is required"
  ),
  institutionApproved: Yup.string().required(
    "Please select institution approval"
  ),
  courseApproved: Yup.string().required("Please select course approval"),
  courseRelevant: Yup.string().required("Please select course relevance"),
  otherNOCGranted: Yup.string().required("Please select NOC status"),
  proposedCourseLocation: Yup.string().required(
    "Please select course location"
  ),
  courseEndingDate: Yup.string().required("Course ending date is required"),
  modeOfCourse: Yup.string().required("Mode of course is required"),
  timingOfClasses: Yup.string().required("Timing of classes is required"),
});

interface NOCHigherStudiesFormProps { }

const NOCHigherStudiesForm = (props: NOCHigherStudiesFormProps) => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();
  const employeesState = useSelector((state: any) => state.employeesState);
  const nocHigherEducationState = useSelector(
    (state: any) => state.nocHigherEducationState
  );
  const isEditMode = Boolean(id);
  const user = employeesState?.data?.data;
  const higherEducationData = nocHigherEducationState?.currentRequest;

  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    dateOfJoining: "",
    employmentStatus: "Direct recruit",
    department: "",
    contactNumber: "",
    email: "",
    dateOfBirth: "",
    probationConfirmed: "",
    currentlyPursuingA: "",
    natureOfDutyPerforming: "",
    nameOfStudyCourse: "",
    typeOfCourse: "",
    durationMonths: "",
    durationYears: "",
    dateOfJoiningCourse: "",
    courseEndingDate: "",
    modeOfCourse: "",
    timingOfClasses: "",
    institutionName: "",
    lastDateRegistration: "",
    institutionApproved: "",
    courseApproved: "",
    courseRelevant: "",
    otherNOCGranted: "",
    proposedCourseLocation: "",
    anyRemarks: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [showOverviewModal, setShowOverviewModal] = useState(!isEditMode);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Processing...");
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState<"success" | "error">("success");
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [enableRealtimeValidation, setEnableRealtimeValidation] =
    useState(false);

  // Master data state
  const [natureOfDutyOptions, setNatureOfDutyOptions] = useState<
    Array<{
      nature_of_duty_id: string;
      nature_of_duty_title: string;
    }>
  >([]);
  const [isLoadingNatureOfDuty, setIsLoadingNatureOfDuty] = useState(false);
  const [courseTypeOptions, setCourseTypeOptions] = useState<
    Array<{ course_type_id: string; course_type_title: string }>
  >([]);
  const [isLoadingCourseTypes, setIsLoadingCourseTypes] = useState(false);
  const [modeOfCourseOptions, setModeOfCourseOptions] = useState<
    Array<{ id: number; course_mode_id: string; course_mode_title: string }>
  >([]);
  const [isLoadingModeOfCourse, setIsLoadingModeOfCourse] = useState(false);

  // Document upload state
  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{
      file: File;
      key: string;
      url: string;
      uploading?: boolean;
      error?: string;
    }>
  >([]);

  // Declaration state
  const [declarationAccepted, setDeclarationAccepted] = useState(false);

  // Modal states for validation messages
  const [showProbationModal, setShowProbationModal] = useState(false);
  const [showPursuingModal, setShowPursuingModal] = useState(false);
  const [showOtherCourseTypeInput, setShowOtherCourseTypeInput] =
    useState(false);
  const [otherCourseType, setOtherCourseType] = useState("");

  // Fetch Nature of Duty options
  const fetchNatureOfDutyOptions = async () => {
    setIsLoadingNatureOfDuty(true);
    try {
      const response = await Api.post(API_URLS.MASTERS.NATURE_OF_DUTY, {});
      if (response?.data?.status === "success" && response?.data?.data) {
        setNatureOfDutyOptions(response.data.data);
      } else if (response?.data?.data) {
        setNatureOfDutyOptions(response.data.data);
      }
    } catch (error: any) {
      console.error("Error fetching nature of duty options:", error);
      toast.error("Failed to load nature of duty options");
    } finally {
      setIsLoadingNatureOfDuty(false);
    }
  };

  // Fetch Course Type options
  const fetchCourseTypeOptions = async () => {
    setIsLoadingCourseTypes(true);
    try {
      const response = await Api.post(API_URLS.MASTERS.COURSE_TYPES, {});
      if (response?.data?.status === "success" && response?.data?.data) {
        setCourseTypeOptions(response.data.data);
      } else if (response?.data?.data) {
        setCourseTypeOptions(response.data.data);
      }
    } catch (error: any) {
      console.error("Error fetching course type options:", error);
      toast.error("Failed to load course type options");
    } finally {
      setIsLoadingCourseTypes(false);
    }
  };

  // Fetch Mode of Course options
  const fetchModeOfCourseOptions = async () => {
    setIsLoadingModeOfCourse(true);
    try {
      const response = await Api.post(API_URLS.MASTERS.MODE_OF_COURSE, {});
      if (response?.data?.status === "success" && response?.data?.data) {
        setModeOfCourseOptions(response.data.data);
      } else if (response?.data?.data) {
        setModeOfCourseOptions(response.data.data);
      }
    } catch (error: any) {
      console.error("Error fetching mode of course options:", error);
      toast.error("Failed to load mode of course options");
    } finally {
      setIsLoadingModeOfCourse(false);
    }
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setValidationErrors({});
      return true;
    } catch (error: any) {
      const errors: { [key: string]: string } = {};
      error.inner.forEach((err: any) => {
        if (err.path) errors[err.path] = err.message;
      });
      setValidationErrors(errors);
      return false;
    }
  };

  useEffect(() => {
    dispatch(getMyProfile({}));
    fetchNatureOfDutyOptions();
    fetchCourseTypeOptions();
    fetchModeOfCourseOptions();
    const timeout = setTimeout(() => setIsLoadingProfile(false), 10000);
    return () => clearTimeout(timeout);
  }, [dispatch]);

  // Fetch Higher Education request data in edit mode
  useEffect(() => {
    if (id) {
      setShowOverviewModal(false);
      dispatch(fetchNOCHigherEducationRequest({ id: parseInt(id) }));
    } else {
      setShowOverviewModal(true);
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (user?.details) {
      const userDetails = user.details;
      setFormData((prev) => ({
        ...prev,
        name: `${userDetails.firstname || ""} ${userDetails.lsastname || userDetails.lastname || ""
          }`.trim(),
        designation:
          userDetails.sap_department_designation_wings?.designation_name ||
          userDetails.designation_details?.designationName ||
          `Designation ID: ${userDetails.designationID || ""}`,
        dateOfJoining: userDetails.date_of_joining
          ? moment(userDetails.date_of_joining).format("DD-MM-YYYY")
          : "",
        employmentStatus:
          userDetails.employmentType || user.employmentType || user.status,
        department:
          userDetails.sap_department_designation_wings?.department_name ||
          userDetails.department_details?.departmentName ||
          `Department ID: ${userDetails.department || ""}`,
        contactNumber: userDetails.phone || "",
        email: userDetails.email || "",
        dateOfBirth: userDetails.birthdate
          ? moment(userDetails.birthdate).format("DD-MM-YYYY")
          : "",
      }));
      setIsLoadingProfile(false);
    } else if (employeesState?.loading === "fulfilled" && !user?.details) {
      setIsLoadingProfile(false);
    }
  }, [user, employeesState?.loading]);

  // Populate form data from higherEducationData in edit mode
  useEffect(() => {
    if (higherEducationData && isEditMode) {
      if (higherEducationData.otherThanDiploma) {
        setOtherCourseType(higherEducationData.otherThanDiploma);
        setShowOtherCourseTypeInput(true);
      }
      setFormData((prev) => ({
        ...prev,
        contactNumber: higherEducationData?.contactNumber || higherEducationData?.contact_number || prev.contactNumber,
        department: higherEducationData?.department || prev.department,
        email: higherEducationData?.emailId || higherEducationData?.email || prev.email,
        probationConfirmed:
          higherEducationData.probationConfirmed === "Y" ? "Yes" : "No",
        currentlyPursuingA:
          higherEducationData.currentlyPursuingA === "Y" ? "Yes" : "No",
        natureOfDutyPerforming: higherEducationData.natureOfDutyPerfo || "",
        nameOfStudyCourse: higherEducationData.courseName || "",
        typeOfCourse: higherEducationData.diplomaOrDegree || "",
        durationMonths: higherEducationData.monthsCourseDuration || "",
        durationYears: higherEducationData.durationYears || "",
        dateOfJoiningCourse: higherEducationData.dateOfCourseJoining || "",
        courseEndingDate: higherEducationData.courseEndingDate || "",
        modeOfCourse: higherEducationData.partTimeOrCorresp || "",
        timingOfClasses: higherEducationData.timingOfClasses || "",
        institutionName: higherEducationData.institute || "",
        lastDateRegistration: higherEducationData.lastDateOfRegistration || "",
        institutionApproved:
          higherEducationData.isGovernmentBody === "Y" ? "Yes" : "No",
        courseApproved:
          higherEducationData.isCourseRecogByGov === "Y" ? "Yes" : "No",
        courseRelevant:
          higherEducationData.isReleventToEmployee === "Y" ? "Yes" : "No",
        otherNOCGranted:
          higherEducationData.whetherAnotherNoc === "Y" ? "Yes" : "No",
        proposedCourseLocation:
          higherEducationData.inIndiaOrNot === "I" || higherEducationData.inIndiaOrNot === "Y" ? "India" : "Outside India",
        anyRemarks: higherEducationData.remarks || "",
      }));
    }
  }, [higherEducationData, isEditMode]);

  // Populate uploaded files from existing documents in edit mode
  useEffect(() => {
    if (higherEducationData && isEditMode) {
      console.log("Higher Education Data:", higherEducationData);
      console.log("Available keys:", Object.keys(higherEducationData));

      // Try different possible field names for documents
      const existingDocuments =
        (higherEducationData as any).noc_documents ||
        (higherEducationData as any).documents ||
        (higherEducationData as any).attachments ||
        (higherEducationData as any).files ||
        [];

      console.log("Existing Documents:", existingDocuments);

      if (Array.isArray(existingDocuments) && existingDocuments.length > 0) {
        const transformedFiles = existingDocuments.map((doc: any) => {
          console.log("Document:", doc);
          // Create a mock File object for display purposes with proper size
          const fileName =
            doc.fileName || doc.file_name || doc.name || "document.pdf";
          const fileSize = doc.fileSize || doc.file_size || doc.size || 0;

          // Create a blob with approximate size to simulate file size
          const blob = new Blob([new ArrayBuffer(fileSize)], {
            type: "application/octet-stream",
          });
          const mockFile = new File([blob], fileName, {
            type: "application/octet-stream",
          });

          return {
            file: mockFile,
            key: doc.fileName || doc.file_name || doc.name || "",
            url: doc.fileUrl || doc.file_url || doc.url || "",
            uploading: false,
          };
        });

        console.log("Transformed Files:", transformedFiles);
        setUploadedFiles(transformedFiles);
      }
    }
  }, [higherEducationData, isEditMode]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    // Check if "Other" is selected for typeOfCourse
    if (name === "typeOfCourse") {
      const selectedOption = courseTypeOptions.find(
        (opt) => opt.course_type_id === value
      );
      if (selectedOption?.course_type_title.toLowerCase() === "other") {
        setShowOtherCourseTypeInput(true);
      } else {
        setShowOtherCourseTypeInput(false);
        setOtherCourseType("");
      }
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to convert file to base64"));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Upload file to AWS
  const uploadFile = async (
    file: File
  ): Promise<{ key: string; url: string } | null> => {
    try {
      const base64String = await fileToBase64(file);
      const result = await dispatch(
        uploadFileToAWS({ filestring: base64String })
      ).unwrap();
      return {
        key: result.data.key,
        url: result.data.url,
      };
    } catch (error) {
      console.error("File upload failed:", error);
      return null;
    }
  };

  // File change handler with automatic AWS upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    // Add files to local state with uploading status
    const newFiles = fileArray.map((file) => ({
      file,
      key: "",
      url: "",
      uploading: true,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // Upload each file to AWS
    for (let i = 0; i < newFiles.length; i++) {
      const fileInfo = newFiles[i];
      try {
        const uploadResult = await uploadFile(fileInfo.file);

        if (uploadResult) {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.file === fileInfo.file
                ? {
                  ...f,
                  key: uploadResult.key,
                  url: uploadResult.url,
                  uploading: false,
                }
                : f
            )
          );
          // Show success toast for individual file upload
          toast.success(`"${fileInfo.file.name}" uploaded successfully`);
        } else {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.file === fileInfo.file
                ? { ...f, error: "Upload failed", uploading: false }
                : f
            )
          );
          // Show error toast for individual file upload failure
          toast.error(`Failed to upload "${fileInfo.file.name}"`);
        }
      } catch (error) {
        console.error("File upload error:", error);
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.file === fileInfo.file
              ? { ...f, error: "Upload failed", uploading: false }
              : f
          )
        );
        // Show error toast for individual file upload failure
        toast.error(`Failed to upload "${fileInfo.file.name}"`);
      }
    }

    // Clear the input so the same file can be uploaded again if needed
    e.target.value = "";
  };

  // Remove file and delete from AWS
  const removeFile = async (fileIndex: number) => {
    const fileToRemove = uploadedFiles[fileIndex];

    if (fileToRemove.key) {
      try {
        await dispatch(deleteFileFromAWS({ key: fileToRemove.key })).unwrap();
        toast.success(`"${fileToRemove.file.name}" deleted successfully`);
      } catch (error) {
        console.error("Failed to delete file from AWS:", error);
        toast.error(`Failed to delete "${fileToRemove.file.name}" from server`);
      }
    }

    setUploadedFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  // Handle file view - get presigned URL and open in new tab
  const handleAttachmentClick = async (attachmentKey: string) => {
    try {
      // Dispatch the Redux action to get presigned URL
      const resultAction = await dispatch(getPresignedUrl(attachmentKey));

      if (getPresignedUrl.fulfilled.match(resultAction)) {
        // Open the file in a new tab using the signed URL
        window.open(resultAction.payload, "_blank");
      } else {
        console.error("Failed to get presigned URL:", resultAction.payload);
        toast.error("Unable to open file. Please try again.");
      }
    } catch (error) {
      console.error("Error opening attachment:", error);
      toast.error("Unable to open file. Please try again.");
    }
  };

  const handleRadioChange = (name: string, value: string) => {
    // Check for probation confirmation
    if (name === "probationConfirmed" && value === "No") {
      setShowProbationModal(true);
      return; // Don't set the value, show modal instead
    }

    // Check for currently pursuing status
    if (name === "currentlyPursuingA" && value === "Yes") {
      setShowPursuingModal(true);
      return; // Don't set the value, show modal instead
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const preparePayload = (isDraft: boolean) => {
    // Ensure uploadedFiles is always an array
    const filesArray = Array.isArray(uploadedFiles) ? uploadedFiles : [];

    const documentsArray = filesArray
      .filter((file) => file.key)
      .map((file) => ({
        fileName: file.key,
        typeOfAttachedDoc: "Upload Documents",
      }));

    // Base payload with required fields
    const payload: any = {
      is_draft: isDraft,
      contactNumber: formData.contactNumber || user?.details?.phone || "",
      department: formData.department || user?.details?.sap_department_designation_wings?.department_name || user?.details?.department_details?.departmentName || "",
      emailId: formData.email || user?.details?.email || "",
      // validation_bypass: true,
    };

    // Only add ID if in edit mode
    if (id) {
      payload.id = parseInt(id);
    }

    // Only include fields that have values
    if (formData.probationConfirmed === "Yes") {
      payload.probationConfirmed = "Y";
    } else if (formData.probationConfirmed === "No") {
      payload.probationConfirmed = "N";
    }

    if (formData.currentlyPursuingA === "Yes") {
      payload.currentlyPursuingA = "Y";
    } else if (formData.currentlyPursuingA === "No") {
      payload.currentlyPursuingA = "N";
    }

    if (formData.natureOfDutyPerforming) {
      payload.natureOfDutyPerfo = formData.natureOfDutyPerforming;
    }

    if (formData.otherNOCGranted === "Yes") {
      payload.whetherAnotherNoc = "Y";
    } else if (formData.otherNOCGranted === "No") {
      payload.whetherAnotherNoc = "N";
    }

    if (formData.nameOfStudyCourse) {
      payload.courseName = formData.nameOfStudyCourse;
    }

    if (formData.typeOfCourse) {
      payload.diplomaOrDegree = formData.typeOfCourse;
    }

    if (formData.modeOfCourse) {
      payload.partTimeOrCorresp = formData.modeOfCourse;
    }

    if (formData.durationYears) {
      payload.durationYears = formData.durationYears;
    }

    if (formData.durationMonths) {
      payload.monthsCourseDuration = formData.durationMonths;
    }

    if (formData.dateOfJoiningCourse) {
      payload.dateOfCourseJoining = formData.dateOfJoiningCourse;
    }

    if (formData.courseEndingDate) {
      payload.courseEndingDate = formData.courseEndingDate;
    }

    if (formData.timingOfClasses) {
      payload.timingOfClasses = formData.timingOfClasses;
    }

    if (formData.institutionName) {
      payload.institute = formData.institutionName;
    }

    if (formData.courseApproved === "Yes") {
      payload.isCourseRecogByGov = "Y";
    } else if (formData.courseApproved === "No") {
      payload.isCourseRecogByGov = "N";
    }

    if (formData.institutionApproved === "Yes") {
      payload.isGovernmentBody = "Y";
    } else if (formData.institutionApproved === "No") {
      payload.isGovernmentBody = "N";
    }

    if (formData.proposedCourseLocation === "India" || formData.proposedCourseLocation === "I" || formData.proposedCourseLocation === "Y") {
      payload.inIndiaOrNot = "I";
    } else if (formData.proposedCourseLocation === "Outside India" || formData.proposedCourseLocation === "O" || formData.proposedCourseLocation === "N") {
      payload.inIndiaOrNot = "O";
    } else {
      payload.inIndiaOrNot = "I";
    }

    const otherTypeVal = otherCourseType || (formData as any).specifyOtherType || (formData as any).otherThanDiploma;
    if (otherTypeVal) {
      payload.otherThanDiploma = otherTypeVal;
    } else {
      payload.otherThanDiploma = "";
    }

    if (formData.lastDateRegistration) {
      payload.lastDateOfRegistration = formData.lastDateRegistration;
    }

    if (formData.courseRelevant === "Yes") {
      payload.isReleventToEmployee = "Y";
    } else if (formData.courseRelevant === "No") {
      payload.isReleventToEmployee = "N";
    }

    payload.remarks = formData.anyRemarks || "";

    // Only include documents if there are any
    if (documentsArray.length > 0) {
      payload.documents = documentsArray;
    }

    console.log("=== PAYLOAD DEBUG ===");
    console.log("Uploaded files state:", uploadedFiles);
    console.log("Files array:", filesArray);
    console.log("Documents count:", documentsArray.length);
    console.log("Documents array:", documentsArray);
    console.log("Full payload:", JSON.stringify(payload, null, 2));
    console.log("===================");

    return payload;
  };

  const handleSaveDraft = async () => {
    // Validate required fields for draft
    if (!formData.nameOfStudyCourse || !formData.nameOfStudyCourse.trim()) {
      toast.error("Course Name is required to save as draft");
      return;
    }

    if (!formData.institutionName || !formData.institutionName.trim()) {
      toast.error("Institute is required to save as draft");
      return;
    }

    setIsSubmitting(true);
    setShowLoadingModal(true);
    setLoadingMessage(isEditMode ? "Updating draft..." : "Saving draft...");

    try {
      const payload = preparePayload(true);
      const response = await Api.post(
        API_URLS.NOC.CREATE_OR_UPDATE_HIGHER_EDUCATION,
        payload
      );

      // Check for success first (status 200 in data.status or statusCode)
      if (
        response?.data?.status === 200 ||
        response?.data?.statusCode === 200 ||
        response?.statusCode === 200
      ) {
        // Extract success message from response
        const successMessage =
          response?.data?.data?.message ||
          response?.data?.message ||
          response?.message ||
          (isEditMode
            ? "Draft updated successfully!"
            : "Draft saved successfully!");
        toast.success(successMessage);
        setTimeout(() => {
          navigate(-1);
        }, 1500);
        return;
      }

      // Check if response contains error status
      if (response?.data?.status === 400 || response?.data?.status === 500) {
        const errorMessage =
          response?.data?.data?.message ||
          response?.data?.message ||
          "Failed to save draft";
        toast.error(errorMessage);
        return;
      }

      // Fallback error
      const errorMessage =
        response?.data?.data?.message ||
        response?.data?.message ||
        response?.message ||
        "Failed to save draft";
      toast.error(errorMessage);
    } catch (error: any) {
      console.error("Error saving draft:", error);

      // Extract error message from different possible response structures
      const errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Failed to save draft. Please try again.";
      toast.error(errorMessage);
    } finally {
      setShowLoadingModal(false);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnableRealtimeValidation(true);
    const isValid = await validateForm();
    if (!isValid) {
      toast.error("Please fix the validation errors before submitting");
      return;
    }

    // Check if user has been in DMRC for at least 2 years
    if (formData.dateOfJoining) {
      const joiningDate = moment(formData.dateOfJoining, "DD-MM-YYYY");
      const currentDate = moment();
      const yearsInDMRC = currentDate.diff(joiningDate, "years", true);

      if (yearsInDMRC < 2) {
        toast.error(
          "You must have completed at least 2 years of service in DMRC to apply for NOC for Higher Studies"
        );
        return;
      }
    }

    // Check if at least one approval (Institution or Course) is "Yes"
    if (formData.institutionApproved !== "Yes" && formData.courseApproved !== "Yes") {
      toast.error(
        "Either the Institution or the Course must be approved by a concerned Government Body (UGC/AICTE/DEC)"
      );
      return;
    }

    // Check declaration
    if (!declarationAccepted) {
      toast.error("Please accept the declaration before submitting");
      return;
    }

    setIsSubmitting(true);
    setShowLoadingModal(true);
    setLoadingMessage(
      isEditMode ? "Updating request..." : "Submitting request..."
    );

    try {
      const payload = preparePayload(false);
      const response = await Api.post(
        API_URLS.NOC.CREATE_OR_UPDATE_HIGHER_EDUCATION,
        payload
      );

      // Check for success first (status 200 in data.status or statusCode)
      if (
        response?.data?.status === 200 ||
        response?.data?.statusCode === 200 ||
        response?.statusCode === 200
      ) {
        // Extract success message from response
        const rawMsg =
          response?.data?.data?.message ||
          response?.data?.message ||
          response?.message;

        const successMessage =
          rawMsg &&
          rawMsg.toLowerCase() !== "noc saved" &&
          rawMsg.toLowerCase() !== "saved successfully" &&
          rawMsg.toLowerCase() !== "created successfully"
            ? rawMsg
            : isEditMode
            ? "NOC application updated successfully!"
            : "Created successfully!";

        setResponseMessage(successMessage);
        setResponseType("success");

        setTimeout(() => {
          setShowLoadingModal(false);
          setShowResponseModal(true);
        }, 500);
        return;
      }

      // Check if response contains error status
      if (response?.data?.status === 400 || response?.data?.status === 500) {
        const errorMessage =
          response?.data?.data?.message ||
          response?.data?.message ||
          "Failed to submit request";

        setResponseMessage(errorMessage);
        setResponseType("error");
        setShowLoadingModal(false);
        setShowResponseModal(true);
        setIsSubmitting(false);
        return;
      }

      // Fallback error
      const errorMessage =
        response?.data?.data?.message ||
        response?.data?.message ||
        response?.message ||
        "Failed to submit request";

      setResponseMessage(errorMessage);
      setResponseType("error");
      setShowLoadingModal(false);
      setShowResponseModal(true);
      setIsSubmitting(false);
    } catch (error: any) {
      console.error("Error submitting request:", error);

      // Extract error message from different possible response structures
      const errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Failed to submit request. Please try again.";

      setResponseMessage(errorMessage);
      setResponseType("error");
      setShowLoadingModal(false);
      setShowResponseModal(true);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <div className="d-flex align-items-center">
                <Link
                  to="#"
                  className="text-primary text-decoration-none me-3"
                  onClick={() => navigate(-1)}
                >
                  <i className="ti ti-arrow-left fs-24"></i>
                </Link>
                <h3 className="page-title mb-0">
                  {isEditMode
                    ? "Edit NOC for Higher Studies"
                    : "Application for NOC for Higher Studies"}
                </h3>
              </div>
              <nav className="mt-2">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.employeeDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.noc.main}>NOC Services</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.noc.higherEducation}>
                      Higher Education
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEditMode ? "Edit Request" : "Create Request"}
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              {isLoadingProfile ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading profile...</span>
                  </div>
                  <span className="ms-2">Loading your profile data...</span>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Employee Details */}
                  <div className="card mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Employee Details</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        {[
                          { label: "Name", name: "name", value: formData.name },
                          {
                            label: "Designation",
                            name: "designation",
                            value: formData.designation,
                          },
                          {
                            label: "Date of Joining in DMRC",
                            name: "dateOfJoining",
                            value: formData.dateOfJoining,
                          },
                          {
                            label: "Employment Status",
                            name: "employmentStatus",
                            value: formData.employmentStatus,
                          },
                          {
                            label: "Department",
                            name: "department",
                            value: formData.department,
                            required: true,
                            editable: true,
                          },
                          {
                            label: "Contact Number",
                            name: "contactNumber",
                            value: formData.contactNumber,
                            required: true,
                            type: "tel",
                            editable: true,
                          },
                          {
                            label: "Email ID",
                            name: "email",
                            value: formData.email,
                            required: true,
                            type: "email",
                            editable: true,
                          },
                          // {
                          //   label: "Date of Birth",
                          //   name: "dateOfBirth",
                          //   value: formData.dateOfBirth,
                          // },
                        ].map((field) => (
                          <div key={field.name} className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                {field.label}{" "}
                                {field.required && (
                                  <span className="text-danger">*</span>
                                )}
                              </label>
                              <input
                                type={field.type || "text"}
                                className="form-control"
                                name={field.name}
                                value={field.value}
                                onChange={handleInputChange}
                                required={field.required}
                                style={!field.editable ? { backgroundColor: "#f8f9fa" } : {}}
                                readOnly={!field.editable}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="card mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Course Details</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Whether probation is confirmed in DMRC{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div>
                              {["Yes", "No"].map((option) => (
                                <div
                                  className="form-check form-check-inline"
                                  key={option}
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="probationConfirmed"
                                    id={`probationConfirmed_${option}`}
                                    value={option}
                                    checked={
                                      formData.probationConfirmed === option
                                    }
                                    onChange={() =>
                                      handleRadioChange(
                                        "probationConfirmed",
                                        option
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`probationConfirmed_${option}`}
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                            {validationErrors.probationConfirmed &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.probationConfirmed}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Whether currently pursuing any Higher Education
                              Course, for which NOC from DMRC was obtained{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div>
                              {["Yes", "No"].map((option) => (
                                <div
                                  className="form-check form-check-inline"
                                  key={option}
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="currentlyPursuingA"
                                    id={`currentlyPursuingA_${option}`}
                                    value={option}
                                    checked={
                                      formData.currentlyPursuingA === option
                                    }
                                    onChange={() =>
                                      handleRadioChange(
                                        "currentlyPursuingA",
                                        option
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`currentlyPursuingA_${option}`}
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                            {validationErrors.currentlyPursuingA &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.currentlyPursuingA}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Nature of duty performing{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="natureOfDutyPerforming"
                              value={formData.natureOfDutyPerforming}
                              onChange={handleInputChange}
                              disabled={isLoadingNatureOfDuty}
                            >
                              <option value="">
                                {isLoadingNatureOfDuty
                                  ? "Loading..."
                                  : "Select"}
                              </option>
                              {natureOfDutyOptions.map((option) => (
                                <option
                                  key={option.nature_of_duty_id}
                                  value={option.nature_of_duty_id}
                                >
                                  {option.nature_of_duty_title}
                                </option>
                              ))}
                            </select>
                            {validationErrors.natureOfDutyPerforming &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.natureOfDutyPerforming}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Name of the study course{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="nameOfStudyCourse"
                              value={formData.nameOfStudyCourse}
                              onChange={handleInputChange}
                              placeholder="Enter course name"
                            />
                            {validationErrors.nameOfStudyCourse &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.nameOfStudyCourse}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Type of Course{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="typeOfCourse"
                              value={formData.typeOfCourse}
                              onChange={handleInputChange}
                              disabled={isLoadingCourseTypes}
                            >
                              <option value="">
                                {isLoadingCourseTypes ? "Loading..." : "Select"}
                              </option>
                              {courseTypeOptions.map((option) => (
                                <option
                                  key={option.course_type_id}
                                  value={option.course_type_id}
                                >
                                  {option.course_type_title}
                                </option>
                              ))}
                            </select>
                            {validationErrors.typeOfCourse &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.typeOfCourse}
                                </div>
                              )}
                          </div>

                          {/* Show "Other" text input if "Other" is selected */}
                          {showOtherCourseTypeInput && (
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Specify the type of course{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={otherCourseType}
                                onChange={(e) =>
                                  setOtherCourseType(e.target.value)
                                }
                                placeholder="Please specify the course type"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Duration of course - Months{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className={`form-control ${
                                validationErrors.durationMonths &&
                                enableRealtimeValidation
                                  ? "is-invalid"
                                  : ""
                              }`}
                              name="durationMonths"
                              value={formData.durationMonths}
                              onChange={handleInputChange}
                              placeholder="00"
                              min="0"
                            />
                            {validationErrors.durationMonths &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.durationMonths}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Years
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              name="durationYears"
                              value={formData.durationYears}
                              onChange={handleInputChange}
                              placeholder="0"
                              min="0"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Date of joining course{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <ConfigProvider
                              theme={{
                                token: {
                                  colorPrimary: "#dc3545",
                                  borderRadius: 6,
                                },
                              }}
                            >
                              <DatePicker
                                className="form-control"
                                value={
                                  formData.dateOfJoiningCourse
                                    ? dayjs(formData.dateOfJoiningCourse)
                                    : null
                                }
                                onChange={(date) => {
                                  handleInputChange({
                                    target: {
                                      name: "dateOfJoiningCourse",
                                      value: date
                                        ? date.format("YYYY-MM-DD")
                                        : "",
                                    },
                                  } as any);
                                }}
                                format="DD-MM-YYYY"
                                placeholder="Select date"
                                style={{
                                  width: "100%",
                                  height: "42px",
                                  fontSize: "14px",
                                }}
                                allowClear={true}
                                showToday={true}
                              />
                            </ConfigProvider>
                            {validationErrors.dateOfJoiningCourse &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.dateOfJoiningCourse}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Course Ending Date (Tentative){" "}
                              <span className="text-danger">*</span>
                            </label>
                            <ConfigProvider
                              theme={{
                                token: {
                                  colorPrimary: "#dc3545",
                                  borderRadius: 6,
                                },
                              }}
                            >
                              <DatePicker
                                className="form-control"
                                value={
                                  formData.courseEndingDate
                                    ? dayjs(formData.courseEndingDate)
                                    : null
                                }
                                onChange={(date) => {
                                  handleInputChange({
                                    target: {
                                      name: "courseEndingDate",
                                      value: date
                                        ? date.format("YYYY-MM-DD")
                                        : "",
                                    },
                                  } as any);
                                }}
                                format="DD-MM-YYYY"
                                placeholder="Select date"
                                style={{
                                  width: "100%",
                                  height: "42px",
                                  fontSize: "14px",
                                }}
                                allowClear={true}
                                showToday={true}
                              />
                            </ConfigProvider>
                            {validationErrors.courseEndingDate &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.courseEndingDate}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Name of the Institution/University{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="institutionName"
                              value={formData.institutionName}
                              onChange={handleInputChange}
                              placeholder="Enter institution name"
                            />
                            {validationErrors.institutionName &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.institutionName}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Mode of course <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="modeOfCourse"
                              value={formData.modeOfCourse}
                              onChange={handleInputChange}
                              disabled={isLoadingModeOfCourse}
                            >
                              <option value="">
                                {isLoadingModeOfCourse
                                  ? "Loading..."
                                  : "Select"}
                              </option>
                              {modeOfCourseOptions.map((option) => (
                                <option
                                  key={option.id}
                                  value={option.course_mode_id}
                                >
                                  {option.course_mode_title}
                                </option>
                              ))}
                            </select>
                            {validationErrors.modeOfCourse &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.modeOfCourse}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Last date of registration for the course{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <ConfigProvider
                              theme={{
                                token: {
                                  colorPrimary: "#dc3545",
                                  borderRadius: 6,
                                },
                              }}
                            >
                              <DatePicker
                                className="form-control"
                                value={
                                  formData.lastDateRegistration
                                    ? dayjs(formData.lastDateRegistration)
                                    : null
                                }
                                onChange={(date) => {
                                  handleInputChange({
                                    target: {
                                      name: "lastDateRegistration",
                                      value: date
                                        ? date.format("YYYY-MM-DD")
                                        : "",
                                    },
                                  } as any);
                                }}
                                format="DD-MM-YYYY"
                                placeholder="Select date"
                                style={{
                                  width: "100%",
                                  height: "42px",
                                  fontSize: "14px",
                                }}
                                allowClear={true}
                                showToday={true}
                              />
                            </ConfigProvider>
                            {validationErrors.lastDateRegistration &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.lastDateRegistration}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Timing of Classes <span className="text-danger">*</span>
                            </label>
                            <ConfigProvider
                              theme={{
                                token: {
                                  colorPrimary: "#dc3545",
                                  borderRadius: 6,
                                },
                              }}
                            >
                              <TimeRangePicker
                                className="form-control d-flex flex-row"
                                value={
                                  formData.timingOfClasses
                                    ? (() => {
                                      try {
                                        // Parse time range like "09:00 AM - 05:00 PM"
                                        const times = formData.timingOfClasses
                                          .split("-")
                                          .map((t) => t.trim());
                                        if (times.length === 2) {
                                          // Parse times - let dayjs handle the format flexibly
                                          const startTime = dayjs(times[0], ["hh:mm A", "h:mm A", "HH:mm"]);
                                          const endTime = dayjs(times[1], ["hh:mm A", "h:mm A", "HH:mm"]);

                                          if (startTime.isValid() && endTime.isValid()) {
                                            return [startTime, endTime] as [dayjs.Dayjs, dayjs.Dayjs];
                                          }
                                        }
                                      } catch (e) {
                                        console.error(
                                          "Error parsing time range:",
                                          e
                                        );
                                      }
                                      return null;
                                    })()
                                    : null
                                }
                                onChange={(times) => {
                                  if (times && times[0] && times[1]) {
                                    // Format times with proper AM/PM
                                    const timeRange = `${times[0].format(
                                      "hh:mm A"
                                    )} - ${times[1].format("hh:mm A")}`;
                                    handleInputChange({
                                      target: {
                                        name: "timingOfClasses",
                                        value: timeRange,
                                      },
                                    } as any);
                                  } else {
                                    handleInputChange({
                                      target: {
                                        name: "timingOfClasses",
                                        value: "",
                                      },
                                    } as any);
                                  }
                                }}
                                use12Hours
                                format="hh:mm A"
                                placeholder={["Start Time", "End Time"]}
                                style={{
                                  width: "100%",
                                  height: "42px",
                                  fontSize: "14px",
                                }}
                                allowClear={true}
                              />
                            </ConfigProvider>
                            {validationErrors.timingOfClasses &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.timingOfClasses}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Whether the Institution is approved by concerned
                              Government Body like UGC/AICTE/DEC{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div>
                              {["Yes", "No"].map((option) => (
                                <div
                                  className="form-check form-check-inline"
                                  key={option}
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="institutionApproved"
                                    id={`institutionApproved_${option}`}
                                    value={option}
                                    checked={
                                      formData.institutionApproved === option
                                    }
                                    onChange={() =>
                                      handleRadioChange(
                                        "institutionApproved",
                                        option
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`institutionApproved_${option}`}
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                            {validationErrors.institutionApproved &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.institutionApproved}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Whether the Course is approved by concerned
                              Government Body like UGC/AICTE/DEC{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div>
                              {["Yes", "No"].map((option) => (
                                <div
                                  className="form-check form-check-inline"
                                  key={option}
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="courseApproved"
                                    id={`courseApproved_${option}`}
                                    value={option}
                                    checked={formData.courseApproved === option}
                                    onChange={() =>
                                      handleRadioChange(
                                        "courseApproved",
                                        option
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`courseApproved_${option}`}
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                            {validationErrors.courseApproved &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.courseApproved}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Is the course relevant to your employment{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div>
                              {["Yes", "No"].map((option) => (
                                <div
                                  className="form-check form-check-inline"
                                  key={option}
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="courseRelevant"
                                    value={option}
                                    checked={formData.courseRelevant === option}
                                    onChange={() =>
                                      handleRadioChange(
                                        "courseRelevant",
                                        option
                                      )
                                    }
                                  />
                                  <label className="form-check-label">
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                            {validationErrors.courseRelevant &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.courseRelevant}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Whether any other NOC have been granted during
                              employment in DMRC for any other course{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div>
                              {["Yes", "No"].map((option) => (
                                <div
                                  className="form-check form-check-inline"
                                  key={option}
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="otherNOCGranted"
                                    value={option}
                                    checked={
                                      formData.otherNOCGranted === option
                                    }
                                    onChange={() =>
                                      handleRadioChange(
                                        "otherNOCGranted",
                                        option
                                      )
                                    }
                                  />
                                  <label className="form-check-label">
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                            {validationErrors.otherNOCGranted &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.otherNOCGranted}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Proposed course is pursuing in{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div>
                              {["India", "Outside India"].map((option) => (
                                <div
                                  className="form-check form-check-inline"
                                  key={option}
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="proposedCourseLocation"
                                    value={option}
                                    checked={
                                      formData.proposedCourseLocation === option
                                    }
                                    onChange={() =>
                                      handleRadioChange(
                                        "proposedCourseLocation",
                                        option
                                      )
                                    }
                                  />
                                  <label className="form-check-label">
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                            {validationErrors.proposedCourseLocation &&
                              enableRealtimeValidation && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.proposedCourseLocation}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Any Remarks
                            </label>
                            <textarea
                              className="form-control"
                              name="anyRemarks"
                              value={formData.anyRemarks}
                              onChange={handleInputChange}
                              rows={3}
                              placeholder="Enter any additional remarks"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Document Upload Section */}
                  <div className="card mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Mandatory Attachments</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <ul className="text-dark mb-3">
                          <li>
                            In case any other NOC has been granted for pursuing
                            higher studies during course of employment in DMRC
                            attach copy of NOC
                          </li>
                          <li>
                            Document indicating last date of registration of
                            course must be attached
                          </li>
                          <li>Last date of admission in brochure</li>
                        </ul>
                      </div>

                      <div className="mb-3">
                        <label className="form-label text-dark fw-medium">
                          Upload Documents{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          onChange={handleFileChange}
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        />
                        <small className="text-muted d-block mt-1">
                          Files will be uploaded automatically upon selection
                        </small>
                      </div>

                      {/* Uploaded Files Table */}
                      {uploadedFiles.length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead style={{ backgroundColor: "#E3F2FD" }}>
                              <tr>
                                <th>Uploaded document</th>
                                <th>File Extension</th>
                                <th>File Size (KB)</th>
                                <th>Status</th>
                                <th>Uploaded by</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {uploadedFiles.map((file, index) => (
                                <tr key={index}>
                                  <td>
                                    {file.uploading ? (
                                      <span className="text-muted">
                                        <i className="ti ti-loader animate-spin me-2"></i>
                                        File {index + 1}
                                      </span>
                                    ) : file.error ? (
                                      <span className="text-danger">
                                        <i className="ti ti-alert-circle me-2"></i>
                                        File {index + 1}
                                      </span>
                                    ) : (
                                      <span className="text-success">
                                        <i className="ti ti-check me-2"></i>
                                        File {index + 1}
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {file.file.name
                                      .split(".")
                                      .pop()
                                      ?.toUpperCase()}
                                  </td>
                                  <td>
                                    {file.file.size > 0
                                      ? (file.file.size / 1024).toFixed(2)
                                      : "N/A"}
                                  </td>
                                  <td>
                                    {file.uploading ? (
                                      <span className="badge bg-warning">
                                        <i className="ti ti-loader animate-spin me-1"></i>
                                        Uploading...
                                      </span>
                                    ) : file.error ? (
                                      <span className="badge bg-danger">
                                        <i className="ti ti-x me-1"></i>
                                        Failed
                                      </span>
                                    ) : (
                                      <span className="badge bg-success">
                                        <i className="ti ti-check me-1"></i>
                                        Uploaded
                                      </span>
                                    )}
                                  </td>
                                  <td>{formData.name}</td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      {file.key &&
                                        !file.uploading &&
                                        !file.error && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              handleAttachmentClick(file.key);
                                            }}
                                            title="View file"
                                          >
                                            <i className="ti ti-eye"></i>
                                          </button>
                                        )}
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => removeFile(index)}
                                        disabled={file.uploading}
                                      >
                                        {file.uploading ? (
                                          <>
                                            <i className="ti ti-loader animate-spin me-1"></i>
                                            Wait...
                                          </>
                                        ) : (
                                          <>
                                            <i className="ti ti-trash me-1"></i>
                                            Delete
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Declaration Section */}
                  <div className="card mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Declaration</h5>
                    </div>
                    <div className="card-body">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="declaration"
                          checked={declarationAccepted}
                          onChange={(e) =>
                            setDeclarationAccepted(e.target.checked)
                          }
                        />
                        <label
                          className="form-check-label text-dark"
                          htmlFor="declaration"
                        >
                          I hereby declare that the information given above is
                          true and my duty hours in DMRC would not get disturbed
                          in any case.I may kindly be granted permission to
                          peruse further studies.
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="card-body">
                      Note: Condition of 03 years Regular Service in DMRC
                      dispensed away with (as per Master Circular).
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="d-flex justify-content-center gap-3 mt-4">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4"
                      onClick={() => navigate(-1)}
                      disabled={isSubmitting}
                    >
                      <i className="ti ti-arrow-left me-1"></i>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary px-4"
                      onClick={handleSaveDraft}
                      disabled={isSubmitting}
                    >
                      <i className="ti ti-device-floppy me-1"></i>
                      Save as Draft
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary px-4"
                      disabled={isSubmitting}
                    >
                      <i className="ti ti-send me-1"></i>
                      Submit Request
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Overview Modal */}
          {showOverviewModal && (
            <>
              <div
                className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                style={{ zIndex: 1040 }}
              />
              <div
                className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
                style={{
                  zIndex: 1050,
                  width: "90%",
                  maxWidth: "700px",
                  maxHeight: "90vh",
                  overflowY: "auto",
                }}
              >
                <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                  <h5 className="mb-0 text-dark fw-bold">
                    NOC for higher studies overview
                  </h5>
                  <button
                    className="btn btn-link text-dark text-decoration-none p-0"
                    onClick={() => setShowOverviewModal(false)}
                  >
                    <i className="ti ti-x fs-24"></i>
                  </button>
                </div>
                <div className="p-4">
                  <h6 className="fw-bold mb-3">
                    CHECK LIST FOR HIGHER QUALIFICATION
                  </h6>
                  <div className="text-dark" style={{ lineHeight: "1.8" }}>
                    <p className="mb-2">
                      <strong>1.</strong> Higher Qualification can be done only
                      after completing one year after probation.
                    </p>
                    <p className="mb-3">
                      १. परीक्षण के बाद एक वर्ष पूरा करने पर उच्च योग्यता
                      प्राप्त की जा सकती है।
                    </p>
                    <p className="mb-2">
                      <strong>2.</strong> The employee shall ensure that the
                      course of study is recognized by concerned Govt. body like
                      AICTE, UGC, DEC etc.
                    </p>
                    <p className="mb-3">
                      २. कर्मचारी यह सुनिश्चित करेगा कि अध्ययन का पाठ्यक्रम
                      संबंधित सरकारी निकायों यथा AICTE, UGC, DEC आदि द्वारा
                      मान्यता प्राप्त है।
                    </p>
                    <p className="mb-2">
                      <strong>3.</strong> Only those employees will be eligible
                      for the incentive if they have secured a minimum of 60%
                      marks or its equivalent grade.
                    </p>
                    <p className="mb-3">
                      ३. केवल वही कर्मचारी प्रोत्साहन राशि के पात्र होंगे
                      जिन्होंने न्यूनतम 60% अंक या इसके समकक्ष ग्रेड प्राप्त
                      किया हो।
                    </p>
                    <p className="mb-2">
                      <strong>4.</strong> Application will be accepted through
                      ESS portal only.
                    </p>
                    <p className="mb-3">
                      ४. आवेदन केवल ईएसएस पोर्टल के माध्यम से ही स्वीकार किया
                      जाएगा।
                    </p>
                    <p className="mb-2">
                      <strong>5.</strong> No employee will be given permission
                      to pursue more than one course at any given part of time.
                    </p>
                    <p className="mb-3">
                      ५. किसी भी कर्मचारी को किसी भी समय एक से अधिक कोर्स करने
                      की अनुमति नहीं दी जाएगी।
                    </p>
                    <p className="mb-2">
                      <strong>6.</strong> Prior approval from the competent
                      authority is required for Higher Education registration.
                    </p>
                    <p className="mb-3">
                      ६. उच्च शिक्षा पंजीकरण के लिए सक्षम प्राधिकारी से पूर्व
                      अनुमोदन अपेक्षित है।
                    </p>
                  </div>
                </div>
                <div className="d-flex justify-content-end p-4 pt-0">
                  <button
                    type="button"
                    className="btn text-white px-5 py-2 fw-bold"
                    style={{
                      backgroundColor: "#B85450",
                      borderRadius: "25px",
                      fontSize: "16px",
                    }}
                    onClick={() => setShowOverviewModal(false)}
                  >
                    OK
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Loading Modal */}
          {showLoadingModal && (
            <>
              <div
                className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                style={{ zIndex: 2000 }}
              />
              <div
                className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
                style={{
                  zIndex: 2001,
                  width: "90%",
                  maxWidth: "400px",
                  padding: "30px",
                }}
              >
                <div className="text-center">
                  <div className="mb-3">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{
                        width: "3rem",
                        height: "3rem",
                        borderWidth: "0.3em",
                      }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                  <h5 className="mb-2 text-dark fw-bold">{loadingMessage}</h5>
                  <p className="text-muted mb-0">
                    Please wait while we process your request...
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Probation Not Confirmed Modal */}
          {showProbationModal && (
            <>
              <div
                className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                style={{ zIndex: 2000 }}
                onClick={() => setShowProbationModal(false)}
              />
              <div
                className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
                style={{
                  zIndex: 2001,
                  width: "90%",
                  maxWidth: "500px",
                }}
              >
                <div
                  className="modal-header border-bottom px-4 py-3"
                  style={{ backgroundColor: "#dc3545" }}
                >
                  <h5 className="modal-title text-white fw-bold">
                    <i className="ti ti-alert-circle me-2"></i>
                    Application Not Allowed
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowProbationModal(false)}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <div
                    className="alert alert-danger mb-0 d-flex align-items-start"
                    role="alert"
                  >
                    <i className="ti ti-alert-triangle me-3 fs-4"></i>
                    <div>
                      <strong className="d-block mb-1">
                        Cannot apply if probation is not confirmed.
                      </strong>
                      <p className="mb-0 small">
                        As per DMRC policy, employees must have their probation
                        confirmed before applying for NOC for Higher Studies.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-top px-4 py-3">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => setShowProbationModal(false)}
                  >
                    I Understand
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Currently Pursuing Course Modal */}
          {showPursuingModal && (
            <>
              <div
                className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                style={{ zIndex: 2000 }}
                onClick={() => setShowPursuingModal(false)}
              />
              <div
                className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
                style={{
                  zIndex: 2001,
                  width: "90%",
                  maxWidth: "500px",
                }}
              >
                <div
                  className="modal-header border-bottom px-4 py-3"
                  style={{ backgroundColor: "#dc3545" }}
                >
                  <h5 className="modal-title text-white fw-bold">
                    <i className="ti ti-alert-circle me-2"></i>
                    Application Not Allowed
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowPursuingModal(false)}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <div
                    className="alert alert-danger mb-0 d-flex align-items-start"
                    role="alert"
                  >
                    <i className="ti ti-alert-triangle me-3 fs-4"></i>
                    <div>
                      <strong className="d-block mb-1">
                        Cannot apply if currently pursuing any Higher Education
                        Course.
                      </strong>
                      <p className="mb-0 small">
                        As per DMRC policy, employees cannot apply for a new NOC
                        if they are already pursuing a Higher Education Course
                        for which NOC from DMRC was obtained.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-top px-4 py-3">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => setShowPursuingModal(false)}
                  >
                    I Understand
                  </button>
                </div>
              </div>
            </>
          )}

          <style>
            {`
            .form-label {
              color: #333 !important;
              font-weight: 500 !important;
              margin-bottom: 8px !important;
              display: block !important;
            }
            .form-check-label {
              color: #333 !important;
              font-weight: 400 !important;
              margin-left: 5px !important;
            }
            .form-control[readonly] {
              background-color: #f8f9fa !important;
              cursor: not-allowed !important;
              border: 1px solid #e9ecef !important;
              color: #6c757d !important;
            }
            .form-control[readonly]:focus {
              border-color: #ced4da !important;
              box-shadow: none !important;
            }
            
            /* DatePicker Styles */
            .ant-picker {
              border: 1px solid #ced4da !important;
              border-radius: 6px !important;
              transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
            }
            .ant-picker:hover {
              border-color: #86b7fe !important;
            }
            .ant-picker-focused {
              border-color: #86b7fe !important;
              box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
            }
            .ant-picker-input > input {
              font-size: 14px !important;
            }
            
            /* Spinner Animation */
            .animate-spin {
              animation: spin 1s linear infinite;
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
          </style>
        </div>
      </div>

      {/* Full Page Loader */}
      <FullPageLoader show={showLoadingModal} message={loadingMessage} />

      {/* Response Modal */}
      {showResponseModal && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
            style={{ zIndex: 2000 }}
          />

          {/* Response Modal */}
          <div
            className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
            style={{
              zIndex: 2001,
              width: "90%",
              maxWidth: "500px",
            }}
          >
            {/* Modal Header */}
            <div
              className={`d-flex align-items-center justify-content-between p-3 border-bottom ${responseType === "success" ? "bg-success" : "bg-info"
                }`}
            >
              <h5 className="mb-0 text-white fw-bold">
                {responseType === "success" ? (
                  <>
                    <i className="ti ti-check-circle me-2"></i>
                    Success
                  </>
                ) : (
                  <>
                    <i className="ti ti-info-circle me-2"></i>
                    Notice
                  </>
                )}
              </h5>
              <button
                className="btn btn-link text-white text-decoration-none p-0"
                onClick={() => {
                  setShowResponseModal(false);
                  if (responseType === "success") {
                    navigate(-1);
                  }
                }}
              >
                <i className="ti ti-x fs-20"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4">
              <div
                className={`p-3 rounded ${responseType === "success"
                  ? "bg-success bg-opacity-10"
                  : "bg-info bg-opacity-10"
                  }`}
              >
                <p className="mb-0 text-dark">{responseMessage}</p>
              </div>

              <div className="d-flex justify-content-center mt-4">
                <button
                  type="button"
                  className={`btn ${responseType === "success" ? "btn-success" : "btn-info"
                    } px-5`}
                  onClick={() => {
                    setShowResponseModal(false);
                    if (responseType === "success") {
                      navigate(-1);
                    }
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NOCHigherStudiesForm;
